// SPDX-License-Identifier: AGPL-3.0-or-later

import Updater from '@app/features/app/state/Updater';
import {makePersistent} from '@app/features/platform/utils/MobXPersistence';
import {
	dismissUpdatePrompt,
	isUpdatePromptVisible,
	resolveUpdatePromptTarget,
	snoozeUpdatePrompt,
	type UpdatePromptRecord,
	type UpdatePromptTarget,
} from '@app/features/updater/utils/UpdatePromptPolicy';
import MediaEngine from '@app/features/voice/engine/MediaEngineFacade';
import {makeAutoObservable, reaction} from 'mobx';

class UpdatePrompt {
	record: UpdatePromptRecord | null = null;
	private now = Date.now();
	private wakeTimer: number | null = null;
	private readonly session = String(Date.now());

	constructor() {
		makeAutoObservable<UpdatePrompt, 'wakeTimer' | 'session'>(
			this,
			{wakeTimer: false, session: false},
			{autoBind: true},
		);
		reaction(() => this.record, this.scheduleWake);
		void makePersistent(this, 'UpdatePrompt', ['record'], {syncAcrossTabs: true});
	}

	get target(): UpdatePromptTarget | null {
		return resolveUpdatePromptTarget({
			nativeReady: Updater.nativeUpdateReady,
			nativeInstalling: Updater.updateInfo.native.installing,
			nativeVersion: Updater.updateInfo.native.version,
			moduleUpdate: Updater.moduleUpdate,
			session: this.session,
			webAvailable: Updater.updateInfo.web.available,
			webVersion: Updater.updateInfo.web.version,
		});
	}

	get visible(): boolean {
		const target = this.target;
		return target != null && isUpdatePromptVisible(target, this.record, this.now);
	}

	get leavesCall(): boolean {
		return MediaEngine.connected || MediaEngine.connecting;
	}

	applyNow(): void {
		void (this.target?.kind === 'web' ? Updater.applyWebUpdate() : Updater.applyUpdate());
	}

	snooze(): void {
		const target = this.target;
		if (!target) return;
		this.now = Date.now();
		this.record = snoozeUpdatePrompt(target, this.record, this.now);
	}

	dismiss(): void {
		const target = this.target;
		if (!target) return;
		this.record = dismissUpdatePrompt(target);
	}

	private scheduleWake(): void {
		if (this.wakeTimer != null) window.clearTimeout(this.wakeTimer);
		this.wakeTimer = null;
		const until = this.record?.snoozedUntil;
		if (until == null || this.record?.dismissed) return;
		this.wakeTimer = window.setTimeout(this.tick, Math.max(0, until - Date.now()));
	}

	private tick(): void {
		this.wakeTimer = null;
		this.now = Date.now();
	}
}

export default new UpdatePrompt();
